import { Injectable } from '@angular/core';
import {
  isLocalQuestionImageUrl,
  LocalQuestionImageUrl,
  QuestionImage,
  QuestionImageCacheEntry,
  QuestionImageUrl,
  RemoteQuestionImageUrl,
  SerializedQuestionImageCache,
} from '@examdojo/models/question-image';
import { BehaviorSubject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { QuestionImageUploadService } from './question-image-upload.service';

@Injectable()
export class QuestionImageCacheService {
  constructor(private readonly uploadService: QuestionImageUploadService) {}

  private readonly cache$$ = new BehaviorSubject<Map<LocalQuestionImageUrl, QuestionImageCacheEntry>>(new Map());

  readonly cache$ = this.cache$$.asObservable();

  async getCache(): Promise<SerializedQuestionImageCache> {
    const cache: SerializedQuestionImageCache = {};

    for (const [key, entry] of this.cache$$.value.entries()) {
      try {
        const arrayBuffer = await entry.file.arrayBuffer();
        const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));

        cache[key] = {
          file: {
            name: entry.file.name,
            type: entry.file.type,
            lastModified: entry.file.lastModified,
            size: entry.file.size,
            base64Data: base64,
          },
          remoteUrl: entry.remoteUrl,
        };
      } catch (error) {
        console.error(`Error serializing file for key ${key}:`, error);
      }
    }

    return cache;
  }

  loadFromCache(cache: SerializedQuestionImageCache) {
    const entries = Object.entries(cache ?? {});

    const deserializedEntries: Array<[LocalQuestionImageUrl, QuestionImageCacheEntry]> = [];

    for (const [key, entry] of entries) {
      try {
        const serializedFile = entry.file;
        const binaryString = atob(serializedFile.base64Data);
        const bytes = new Uint8Array(binaryString.length);

        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const file = new File([bytes], serializedFile.name, {
          type: serializedFile.type,
          lastModified: serializedFile.lastModified,
        });

        deserializedEntries.push([key as LocalQuestionImageUrl, { file, remoteUrl: entry.remoteUrl }]);
      } catch (error) {
        console.error(`Error deserializing file for key ${key}:`, error);
      }
    }

    const map = new Map(deserializedEntries);
    this.cache$$.next(map);
  }

  async processImage(image: QuestionImage): Promise<QuestionImage> {
    const processedUrl =
      image.content?.url && isLocalQuestionImageUrl(image.content.url)
        ? await this.processLocalUrl(image.content.url)
        : image.content?.url;

    const processedOriginalUrl =
      image.content?.original_url && isLocalQuestionImageUrl(image.content.original_url)
        ? await this.processLocalUrl(image.content.original_url)
        : image.content?.original_url;

    return {
      ...image,
      content: {
        ...image.content,
        url: processedUrl,
        original_url: processedOriginalUrl,
      },
    };
  }

  getLocalImage(url: LocalQuestionImageUrl): File | undefined {
    return this.cache$$.value.get(url)?.file;
  }

  setLocalImage(
    file: File,
    currentUrl: QuestionImageUrl | null,
    bucketName: string,
    fileName: string = uuid(),
  ): LocalQuestionImageUrl {
    if (currentUrl && isLocalQuestionImageUrl(currentUrl)) {
      const existingCacheEntry = this.cache$$.value.get(currentUrl);

      if (!existingCacheEntry) {
        throw new Error(`No cached file found for upload key ${currentUrl}`);
      }

      const newCache = new Map(this.cache$$.value);
      newCache.set(currentUrl, {
        file,
        remoteUrl: existingCacheEntry.remoteUrl,
      });

      this.cache$$.next(newCache);

      return currentUrl;
    }

    const newCache = new Map(this.cache$$.value);
    const localUrl: LocalQuestionImageUrl = `local://${bucketName}/${fileName}`;

    newCache.set(localUrl, { file, remoteUrl: currentUrl });
    this.cache$$.next(newCache);

    return localUrl;
  }

  removeLocalImage(url: LocalQuestionImageUrl): void {
    const newCache = new Map(this.cache$$.value);
    newCache.delete(url);
    this.cache$$.next(newCache);
  }

  private removeRemoteImage(url: RemoteQuestionImageUrl) {
    return this.uploadService.removeImage(url);
  }

  private async processLocalUrl(url: LocalQuestionImageUrl): Promise<RemoteQuestionImageUrl | LocalQuestionImageUrl> {
    const { file, remoteUrl } = this.cache$$.value.get(url) ?? { file: undefined, remoteUrl: null };

    if (!file) {
      throw new Error(`No cached file found for upload ${url}`);
    }

    const uploadedUrl = await this.uploadService.uploadImage(file, url);

    this.removeLocalImage(url);

    if (remoteUrl) {
      await this.removeRemoteImage(remoteUrl);
    }

    return uploadedUrl;
  }
}
